<template>
  <div v-if="tabs.length" ref="tabs" class="context-tabs">
    <!-- The active tab's background, drawn once and moved, so a change of tab slides it from one
         to the other instead of switching it off here and on there. Measured off the tabs rather
         than derived from them: what it has to match is where the text actually laid out, which
         depends on the labels and the font. -->
    <span
      v-show="indicator.visible"
      class="context-tab-indicator"
      :class="{ 'context-tab-indicator--animated': indicatorAnimated }"
      :style="indicatorStyle"
    ></span>
    <router-link
      v-for="tab in tabs"
      :key="tab.key"
      :to="tab.to"
      class="context-tab"
      :class="{ 'context-tab--active': tab.active }"
      :data-id="tab.dataId"
      >{{ tab.label }}</router-link
    >
  </div>
</template>

<script>
import { buildContextTabs } from "@/advisor/lib/navigation";

// Stable per-tab ids so QA selectors do not move.
const TAB_DATA_IDS = {
  registeredInvestmentAdvisor: "0f3a1c7d5e2b48c9a6d4f8e0b2c5a719",
  advisoryFirm: "7b2e4d9f1a6c43e8b5d0c7a9f3e1b846",
  advisor: "3d8f6b1e4c0a29576e6d4b8c1f3a0e52",
  reports: "9c4a7e2b6d1f45a8c3e9b7d0f2a6c854",
};

// Where the active tab's background was last left, at module scope so that it outlives this
// component. The strip survives a change of route params, the router reusing the view, and the
// background then moves on its own; a change of route builds a new instance, which without this
// would have no position to move from.
//
// Presentational and good for one page load only, which is why it is not in the store.
let lastIndicator = null;

export default {
  data() {
    return {
      indicator: { left: 0, top: 0, width: 0, height: 0, visible: false },
      // Held off until the first measurement has been painted, so the strip appearing does not
      // slide the background in from the left edge. Once on, it stays on.
      indicatorAnimated: false,
    };
  },

  computed: {
    tabs: function () {
      return buildContextTabs(
        this.$store.getters.anchorAccount,
        this.$store.state.masterAccount,
        this.$route,
        this.$store.state.selectedAccountUuidByTier
      ).map((tab) => ({ ...tab, dataId: TAB_DATA_IDS[tab.key] }));
    },

    indicatorStyle: function () {
      return {
        transform: `translate3d(${this.indicator.left}px, ${this.indicator.top}px, 0)`,
        width: `${this.indicator.width}px`,
        height: `${this.indicator.height}px`,
      };
    },
  },

  watch: {
    // On the route, and deliberately not on the tab list the background is measured from: the
    // guard commits the account in context before the navigation completes, so the list changes
    // while the page being left is still on screen. Measuring then would move the background to
    // the tab of a page not yet shown, and on a navigation that rebuilds this component that is
    // what the next instance would read as its starting position.
    $route: "scheduleMeasurement",

    // The strip is not rendered at all until there are tabs, so its first appearance is not
    // necessarily a change of route. The length is what the guard above does not disturb: the
    // account in context moves between tabs without adding or removing any.
    "tabs.length": "scheduleMeasurement",
  },

  methods: {
    // A tick later, because what is measured is where the tabs ended up: the active label is a
    // heavier weight and so a different width, and that is not laid out until the class is on.
    scheduleMeasurement: function () {
      this.$nextTick(this.measureIndicator);
    },

    measureIndicator: function () {
      const container = this.$refs.tabs;
      const active = container
        ? container.querySelector(".context-tab--active")
        : null;

      // A page belonging to none of the tabs. Hidden rather than left where it was, which would
      // mark a tab the user is not on.
      if (!active) {
        this.indicator.visible = false;
        lastIndicator = { ...this.indicator };

        return;
      }

      const firstMeasurement = !this.indicator.visible;

      this.indicator = {
        left: active.offsetLeft,
        top: active.offsetTop,
        width: active.offsetWidth,
        height: active.offsetHeight,
        visible: true,
      };

      lastIndicator = { ...this.indicator };

      if (firstMeasurement) {
        this.$nextTick(() => {
          this.indicatorAnimated = true;
        });
      }
    },
  },

  mounted: function () {
    // Coming from another page: the background is put back where that page left it, and only then
    // moved to this page's tab. Two frames, because a transition runs off the last painted value
    // and measuring in the first would replace the starting position before it was painted.
    if (lastIndicator && lastIndicator.visible) {
      this.indicator = { ...lastIndicator };
      this.indicatorAnimated = true;

      requestAnimationFrame(() => requestAnimationFrame(this.measureIndicator));

      return;
    }

    this.scheduleMeasurement();

    // The labels lay out in a fallback face until Roboto arrives, and are a different width in
    // it, so the background is measured again once it has. Only on a cold load: with the font
    // already loaded the promise is settled, and on a navigation it would resolve ahead of the
    // frames above and replace the position the move was to start from.
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(this.measureIndicator);
    }
  },
};
</script>

<style lang="scss" scoped>
.context-tabs {
  display: flex;
  align-items: center;
  gap: 16px;
  // What the background below is positioned against.
  position: relative;
}

// The active tab's background. Out of flow, so its place among the tabs does not affect the
// layout; the tabs are lifted over it rather than it being pushed under them, because a negative
// z-index here would drop it behind the bar's own background.
.context-tab-indicator {
  position: absolute;
  top: 0;
  left: 0;
  z-index: 0;
  border-radius: 8px;
  background-color: rgba(255, 255, 255, 0.16);
  pointer-events: none;
}

// The first of the three things a context switch animates, so this 900ms is read twice more: the
// drawer's spark delays itself by it to start once this has landed, and the store adds the two of
// them together before saying the toast.
.context-tab-indicator--animated {
  // Fallback for anything without linear(), which the browserslist target still includes: the
  // same 900ms, overshooting once and settling, rather than no movement at all -- an unsupported
  // easing invalidates the whole declaration.
  transition: width 900ms ease-in-out,
    transform 900ms cubic-bezier(0.22, 1.4, 0.36, 1);
  // The design's spring, sampled off its own curve: damped at 7.19 with 3.6 oscillations across
  // the 900ms, overshooting the target by 36% at 126ms. Thirteen stops rather than one per
  // oscillation, the amplitude being under 3% after the first bounce.
  transition: width 900ms ease-in-out,
    transform 900ms
      linear(
        0 0%,
        0.099 2%,
        0.492 5%,
        0.944 8%,
        1.259 11%,
        1.364 14%,
        1.243 18%,
        1.023 22%,
        0.872 27%,
        0.951 34%,
        1.044 44%,
        0.988 60%,
        1 100%
      );
}

.context-tab {
  color: $white;
  text-decoration: none;
  // Above the background that slides between the tabs.
  position: relative;
  z-index: 1;
  font-family: Roboto;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
  letter-spacing: 0.42px;
  padding: 12px 10px;
  border-radius: 8px;
  white-space: nowrap;

  &:hover {
    background-color: hsla(0, 0%, 100%, 0.08);
  }
}

// Vuetify colours every anchor inside .v-application from a selector a bare class cannot
// outweigh, and the tab is a router-link.
.v-application a.context-tab {
  color: $white;
}

// The weight is the half of the active state that belongs to the tab itself; the background is
// the element above, shared between the tabs so it can move from one to the next.
.context-tab--active {
  font-weight: 600;
}
</style>
